import { supabase } from '../supabase.js'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const defaultEventSourceFactory = url => new EventSource(url)

function normalizeInstrumentId(value) {
  const instrumentId = String(value || '').trim().toLowerCase()
  if (!UUID.test(instrumentId)) throw new TypeError('Instrument ID must be a UUID')
  return instrumentId
}

export function createIbkrInstrumentQuoteStore({
  fetchImpl = (...args) => fetch(...args),
  eventSourceFactory = defaultEventSourceFactory,
  debounceMs = 10,
  unsubscribeGraceMs = 30_000,
  activationRetryDelaysMs = [250, 500, 1_000, 2_000, 5_000],
  recoveryDelaysMs = [1_000, 2_000, 5_000],
  onError = () => {},
} = {}) {
  const entries = new Map()
  const storeListeners = new Set()
  const resources = new Set()
  const pendingIds = new Set()
  const activationDelays = activationRetryDelaysMs.length
    ? activationRetryDelaysMs
    : [1_000]
  const recoveryDelays = recoveryDelaysMs.length ? recoveryDelaysMs : [5_000]
  let attachTimer = null
  let attaching = false
  let version = 0

  function report(error) {
    try { onError(error instanceof Error ? error : new Error(String(error))) } catch { /* observer only */ }
  }

  function notify(entry) {
    version += 1
    for (const listener of entry.listeners) {
      try { listener(entry.snapshot) } catch { /* isolate sibling consumers */ }
    }
    for (const listener of storeListeners) {
      try { listener() } catch { /* isolate sibling consumers */ }
    }
  }

  function publish(quote, { force = false } = {}) {
    if (!quote || typeof quote !== 'object') return
    let instrumentId
    try { instrumentId = normalizeInstrumentId(quote.instrumentId) } catch { return }
    const entry = entries.get(instrumentId)
    if (!entry) return
    const sequence = Number(quote.sequence)
    if (!force && (!Number.isFinite(sequence) || sequence <= entry.sequence)) return
    if (Number.isFinite(sequence)) entry.sequence = sequence
    entry.snapshot = Object.freeze({ ...quote, instrumentId })
    notify(entry)
  }

  function publishUnavailable(instrumentId) {
    const entry = entries.get(instrumentId)
    if (!entry) return
    entry.snapshot = Object.freeze({
      instrumentId,
      sequence: entry.sequence,
      freshness: 'unavailable',
    })
    notify(entry)
  }

  function closeResource(resource) {
    if (!resources.delete(resource)) return
    resource.source?.close()
  }

  function closeResources() {
    for (const resource of [...resources]) closeResource(resource)
  }

  function publishStale(instrumentIds) {
    for (const instrumentId of instrumentIds) {
      const entry = entries.get(instrumentId)
      if (!entry) continue
      if (!entry.snapshot || entry.snapshot.freshness === 'unavailable') continue
      entry.snapshot = Object.freeze({ ...entry.snapshot, freshness: 'stale' })
      notify(entry)
    }
  }

  function isCovered(instrumentId) {
    return [...resources].some(resource => resource.instrumentIds.has(instrumentId))
  }

  function scheduleAttach(delay = debounceMs) {
    if (attachTimer || attaching || pendingIds.size === 0) return
    attachTimer = setTimeout(() => {
      attachTimer = null
      void attachPending()
    }, delay)
  }

  function queueInstrument(instrumentId, delay = debounceMs) {
    if (!entries.has(instrumentId) || isCovered(instrumentId)) return
    pendingIds.add(instrumentId)
    scheduleAttach(delay)
  }

  function clearRetry(entry) {
    if (entry.activationTimer) clearTimeout(entry.activationTimer)
    if (entry.recoveryTimer) clearTimeout(entry.recoveryTimer)
    entry.activationTimer = null
    entry.recoveryTimer = null
    entry.activationAttempt = 0
    entry.recoveryAttempt = 0
  }

  function scheduleActivationRetry(instrumentId) {
    const entry = entries.get(instrumentId)
    if (!entry || entry.activationTimer || isCovered(instrumentId)) return
    if (entry.activationAttempt >= activationDelays.length) {
      publishUnavailable(instrumentId)
      return
    }
    const delay = activationDelays[entry.activationAttempt]
    entry.activationAttempt += 1
    entry.activationTimer = setTimeout(() => {
      entry.activationTimer = null
      if (!entries.has(instrumentId) || isCovered(instrumentId)) return
      pendingIds.add(instrumentId)
      void attachPending()
    }, delay)
  }

  function scheduleRecovery(instrumentId) {
    const entry = entries.get(instrumentId)
    if (!entry || entry.recoveryTimer || isCovered(instrumentId)) return
    const delay = recoveryDelays[Math.min(entry.recoveryAttempt, recoveryDelays.length - 1)]
    entry.recoveryAttempt += 1
    entry.recoveryTimer = setTimeout(() => {
      entry.recoveryTimer = null
      if (!entries.has(instrumentId) || isCovered(instrumentId)) return
      pendingIds.add(instrumentId)
      void attachPending()
    }, delay)
  }

  async function attachPending() {
    if (attaching) return
    const instrumentIds = [...pendingIds].filter(instrumentId => (
      entries.has(instrumentId) && !isCovered(instrumentId)
    ))
    pendingIds.clear()
    if (instrumentIds.length === 0) return
    attaching = true
    try {
      const { data: { session } = {} } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Authenticated Erevis session is required for market data')
      const chunks = []
      for (let offset = 0; offset < instrumentIds.length; offset += 64) {
        chunks.push(instrumentIds.slice(offset, offset + 64))
      }
      await Promise.all(chunks.map(async selectedIds => {
        const response = await fetchImpl('/api/ibkr/market-data/instruments', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ instrumentIds: selectedIds }),
        })
        if (!response.ok) throw new Error(`Instrument subscription failed: ${response.status}`)
        const payload = await response.json()
        const acceptedIds = new Set()
        for (const value of payload.acceptedInstrumentIds || []) {
          try {
            const instrumentId = normalizeInstrumentId(value)
            if (entries.has(instrumentId) && selectedIds.includes(instrumentId)) {
              acceptedIds.add(instrumentId)
            }
          } catch { /* malformed upstream ID */ }
        }
        for (const quote of payload.snapshots || []) publish(quote, { force: true })
        if (payload.streamUrl && acceptedIds.size > 0) {
          const source = eventSourceFactory(payload.streamUrl)
          const resource = { instrumentIds: acceptedIds, source }
          resources.add(resource)
          for (const instrumentId of acceptedIds) clearRetry(entries.get(instrumentId))
          const receive = event => {
            if (!resources.has(resource)) return
            try { publish(JSON.parse(event.data)) } catch { /* malformed SSE */ }
          }
          source.addEventListener('message', receive)
          source.addEventListener('quote', receive)
          source.addEventListener('error', () => {
            if (!resources.has(resource)) return
            report(new Error('Instrument market-data stream disconnected'))
            publishStale(resource.instrumentIds)
            closeResource(resource)
            for (const instrumentId of resource.instrumentIds) scheduleRecovery(instrumentId)
          })
        }
        for (const value of payload.rejectedInstrumentIds || []) {
          try { scheduleActivationRetry(normalizeInstrumentId(value)) } catch { /* malformed upstream ID */ }
        }
        for (const instrumentId of selectedIds) {
          if (!isCovered(instrumentId)
            && !(payload.rejectedInstrumentIds || []).includes(instrumentId)) {
            scheduleRecovery(instrumentId)
          }
        }
      }))
    } catch (error) {
      report(error)
      for (const instrumentId of instrumentIds) scheduleRecovery(instrumentId)
    } finally {
      attaching = false
      scheduleAttach(0)
    }
  }

  function retain(value, listener = () => {}) {
    const instrumentId = normalizeInstrumentId(value)
    let entry = entries.get(instrumentId)
    const isNew = !entry
    if (!entry) {
      entry = {
        instrumentId,
        listeners: new Set(),
        snapshot: null,
        sequence: -1,
        releaseTimer: null,
        activationAttempt: 0,
        activationTimer: null,
        recoveryAttempt: 0,
        recoveryTimer: null,
      }
      entries.set(instrumentId, entry)
    }
    if (entry.releaseTimer) {
      clearTimeout(entry.releaseTimer)
      entry.releaseTimer = null
    }
    entry.listeners.add(listener)
    if (entry.snapshot) {
      try { listener(entry.snapshot) } catch { /* isolate this consumer */ }
    }
    if (isNew) queueInstrument(instrumentId)

    let released = false
    return () => {
      if (released) return
      released = true
      entry.listeners.delete(listener)
      if (entry.listeners.size > 0 || entry.releaseTimer) return
      entry.releaseTimer = setTimeout(() => {
        entry.releaseTimer = null
        if (entry.listeners.size > 0) return
        clearRetry(entry)
        entries.delete(instrumentId)
        pendingIds.delete(instrumentId)
        if (entries.size === 0) {
          if (attachTimer) clearTimeout(attachTimer)
          attachTimer = null
          closeResources()
        } else {
          for (const resource of [...resources]) {
            if ([...resource.instrumentIds].some(id => entries.has(id))) continue
            closeResource(resource)
          }
        }
      }, unsubscribeGraceMs)
    }
  }

  return {
    retain,
    getSnapshot(value) {
      try { return entries.get(normalizeInstrumentId(value))?.snapshot ?? null } catch { return null }
    },
    getVersion: () => version,
    subscribe(listener) {
      storeListeners.add(listener)
      return () => storeListeners.delete(listener)
    },
  }
}

export const ibkrInstrumentQuoteStore = createIbkrInstrumentQuoteStore()
